<template v-if="activityTitle && IPadUserPromptViewed">
  <aside
    v-if="!showAssessmentAcceleratorIntro && !showSpotCheckAnimation"
    :class="['activity-intro', { 'keyd-check': isKeyboardCheck }]"
  >
    <h1 :class="['activity-title', { 'keyd-check': isKeyboardCheck }]">
      {{ isKeyboardCheck ? 'Keyboard Check' : activityTitle.display }}
    </h1>
    <h2 class="activity-subtitle" :class="{ 'keyd-check': isKeyboardCheck }">
      Click the arrow to start
    </h2>
    <img
      v-if="!isKeyboardCheck && !showAssessmentAcceleratorIntro"
      :alt="introImageAlt"
      :src="introImage"
      class="activity-image"
    />
    <intro-button :is-loading="isLoading" :end-intro="endIntro"></intro-button>
  </aside>
  <assessment-accelerator-intro
    v-else-if="showAssessmentAcceleratorIntro && !showSpotCheckAnimation"
    :on-complete="handleAnimationComplete"
    :isAssessmentAcceleratorPreview="isAssessmentAcceleratorPreview"
  ></assessment-accelerator-intro>
  <spot-check-animation v-else :on-complete="handleAnimationComplete"></spot-check-animation>
</template>

<script>
import { isEmpty } from 'lodash';
import { TweenMax } from 'gsap';
import { mapGetters, mapState, mapMutations } from 'vuex';
import IntroButton from 'components/buttons/IntroButton.vue';
import AssessmentAcceleratorIntro from './AssessmentAcceleratorIntro.vue';
import SpotCheckAnimation from './SpotCheckAnimation.vue';
import MetaHelper from '../../mixins/MetaHelper';

export default {
  mixins: [MetaHelper],
  components: {
    AssessmentAcceleratorIntro,
    IntroButton,
    SpotCheckAnimation
  },

  computed: {
    ...mapState('application', {
      usingExternalKeyboard: state => state.device.usingExternalKeyboard,
      isMobile: state => state.device.isMobile,
      kwtMode: state => state.kwtMode
    }),

    ...mapState('activity', {
      voiceInstructions: state => state.instructions.voice,
      isSpotCheck: state => Boolean(state.data.spotCheck),
      activityInfo: state => ({
        activityId: state.data.activityId || null,
        scheduledActivityId: state.data.scheduledActivityId || null
      }),
      isLoading: ({ data, dataLoaded, readyToStart }) => {
        return isEmpty(data) || !dataLoaded || !readyToStart;
      }
    }),

    ...mapState('activity', [
      'activityInProgress',
      'delay',
      'instructions',
      'progress',
      'isRestarting',
      'readyToStart'
    ]),

    ...mapGetters('activity', ['activityTitle', 'isSpotCheckActivity', 'templateVideo']),

    IPadUserPromptViewed() {
      return this.isMobile ? this.usingExternalKeyboard !== undefined : true;
    },

    isAssessmentAcceleratorPreview() {
      const { isAssessmentAcceleratorActivity, kwtMode } = this;
      return isAssessmentAcceleratorActivity() && ['preview', 'previewLite'].includes(kwtMode);
    },

    showAssessmentAcceleratorIntro() {
      const { isAssessmentAcceleratorActivity, kwtMode } = this;
      return (
        (this.isScheduledActivity &&
          kwtMode === 'curriculum' &&
          isAssessmentAcceleratorActivity()) ||
        this.isAssessmentAcceleratorPreview
        // || (kwtMode === 'preview' && isSpotCheckActivity)
      );
    },

    showSpotCheckAnimation() {
      const { isSpotCheck, kwtMode } = this;
      const isSpotCheckActivity = ['Letters', 'Paragraph', 'Sentences', 'Words'].includes(
        this.activityTitle.name
      );
      return (
        (kwtMode === 'curriculum' && isSpotCheck) || (kwtMode === 'preview' && isSpotCheckActivity)
      );
    },

    isScheduledActivity() {
      const { scheduledActivityId } = this.activityInfo;
      return Boolean(scheduledActivityId);
    },

    introImage() {
      const {
        activityTitle: { imageFileName }
      } = this;
      if (!this.isKeyboardCheck && Boolean(imageFileName)) {
        const imagePath = require(`images/activity-intro/${imageFileName.toLowerCase()}.png`); //eslint-disable-line
        return imagePath;
      }

      return '';
    },

    introImageAlt() {
      const {
        activityTitle: { display }
      } = this;
      return display ? `${display} activity preview` : '';
    },

    isKeyboardCheck() {
      const isMMM = this.isScheduledActivity && this.activityTitle.name === 'MatManMatch';
      const isSpotCheckActivity =
        ['Letters', 'Paragraph', 'Sentences', 'Words'].indexOf(this.activityTitle.name) >= 0;
      return isSpotCheckActivity || isMMM;
    }
  },

  mounted() {
    if (this.isScheduledActivity || this.isSpotCheck) {
      this.setToolbarMode('fullScreenOnly');
    } else {
      this.setToolbarMode('inActivityPanel');
    }

    this.$nextTick(() => {
      if (this.templateVideo && this.templateVideo.autoplay) {
        this.setModal({
          type: 'video',
          data: this.templateVideo
        });
      }
    });
  },

  beforeDestroy() {
    const fullscreenBtn = document.querySelector('[aria-label="Fullscreen"]');
    if (fullscreenBtn) {
      fullscreenBtn.focus();
    }
  },

  methods: {
    ...mapMutations('application', ['setToolbarMode', 'setModal']),
    ...mapMutations('activity', ['setIntroViewed']),
    ...mapGetters('activity', ['isAssessmentAcceleratorActivity']),

    endIntro() {
      if (this.usingExternalKeyboard) {
        document.getElementById('external-key-input').focus();
      }
      TweenMax.to('.activity-intro', 1, {
        autoAlpha: 0,
        onComplete: this.setIntroViewed
      });
    },

    handleAnimationComplete() {
      this.animationEnded = true;
      if (!this.isLoading) {
        this.endIntro();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-intro {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  background: linear-gradient(0deg, #fff 50%, #1a3963 50%);

  &.keyd-check {
    background: none;
    justify-content: center;
  }

  &.hide {
    display: none;
  }
}

// TODO: refactor styles and HTML for intro for more elegant styling
.activity-title {
  padding: 0;
  color: white;
  position: absolute;
  top: 9%;
  width: 100%;

  &.keyd-check {
    color: black;
    font-size: calc(3vw + 3vh);
    position: static;
    top: 0;
  }
}

.activity-image {
  margin: 0 auto;
  max-width: 100%;
  width: calc(100vw);
}

.activity-subtitle {
  display: none;
  font-family: 'hwtfutura-semibold';
  position: relative;
  top: auto;
  text-align: center;
  padding: 0;
  margin: 0 auto;

  &.keyd-check {
    display: block;
    font-size: calc((3vw + 3vh) / 3);
  }
}
</style>
